import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatDate, PROJECT_STATUSES, StatusBadge } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, Pencil, Trash2, Users, Briefcase, TrendingUp, Wallet, Percent, AlertCircle, Search,
  LayoutGrid, Table as TableIcon, FileText, X, Columns,
} from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_CLIENT = {
  company_name: '', contact_name: '', email: '', phone: '', address: '', siret: '', sector: '', notes: '',
};

const COLUMN_TYPE_LABELS = {
  text: 'Texte',
  number: 'Nombre',
  date: 'Date',
  select: 'Liste déroulante',
};

export default function CommercialPortalPage() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // Clients
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState(INITIAL_CLIENT);
  const [clientSearch, setClientSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all'); // all | followup
  const [sectorFilter, setSectorFilter] = useState('all');
  // Custom pages
  const [customPages, setCustomPages] = useState([]);
  const [activePage, setActivePage] = useState(null); // full page with rows
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [pageForm, setPageForm] = useState({ name: '' });
  const [rowDialogOpen, setRowDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [rowForm, setRowForm] = useState({});
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [columnsDraft, setColumnsDraft] = useState([]);
  const [newCol, setNewCol] = useState({ label: '', type: 'text', options: '' });
  const [renameDialog, setRenameDialog] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [meRes, clientsRes, projectsRes, pagesRes] = await Promise.all([
        api.get('/commercials/me'),
        api.get(`/clients${clientSearch ? `?search=${encodeURIComponent(clientSearch)}` : ''}`),
        api.get('/commercials/me/projects'),
        api.get('/custom-pages'),
      ]);
      setMe(meRes.data);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setCustomPages(pagesRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [clientSearch]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ===== CLIENTS =====
  const uniqueSectors = [...new Set(clients.map(c => c.sector).filter(Boolean))];
  const filteredClients = clients.filter(c => {
    if (clientFilter === 'followup' && !c.needs_followup) return false;
    if (sectorFilter !== 'all' && c.sector !== sectorFilter) return false;
    return true;
  });

  const handleSaveClient = async () => {
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, clientForm);
        toast.success('Client mis à jour');
      } else {
        await api.post('/clients', clientForm);
        toast.success('Client ajouté');
      }
      setClientDialogOpen(false);
      setEditingClient(null);
      setClientForm(INITIAL_CLIENT);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEditClient = (c) => {
    setEditingClient(c);
    setClientForm({
      company_name: c.company_name || '', contact_name: c.contact_name || '',
      email: c.email || '', phone: c.phone || '', address: c.address || '',
      siret: c.siret || '', sector: c.sector || '', notes: c.notes || '',
    });
    setClientDialogOpen(true);
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try { await api.delete(`/clients/${id}`); toast.success('Client supprimé'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // ===== CUSTOM PAGES =====
  const loadPage = async (pageId) => {
    try {
      const { data } = await api.get(`/custom-pages/${pageId}`);
      setActivePage(data);
    } catch { toast.error('Impossible de charger la page'); }
  };

  const handleCreatePage = async () => {
    if (!pageForm.name.trim()) { toast.error('Nom requis'); return; }
    if (customPages.length >= 5) { toast.error('Limite de 5 pages atteinte'); return; }
    try {
      const { data } = await api.post('/custom-pages', { name: pageForm.name.trim() });
      toast.success('Page créée');
      setPageDialogOpen(false);
      setPageForm({ name: '' });
      await fetchAll();
      loadPage(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleRenamePage = async () => {
    if (!renameValue.trim()) { toast.error('Nom requis'); return; }
    try {
      await api.put(`/custom-pages/${renameDialog.id}`, { name: renameValue.trim() });
      toast.success('Page renommée');
      setRenameDialog(null);
      setRenameValue('');
      fetchAll();
      if (activePage?.id === renameDialog.id) loadPage(renameDialog.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeletePage = async (p) => {
    if (!window.confirm(`⚠️ Supprimer la page "${p.name}" ?\n\nToutes les informations du tableau (${p.row_count || 0} ligne(s)) seront effacées définitivement. Cette action est irréversible.`)) return;
    try {
      await api.delete(`/custom-pages/${p.id}`);
      toast.success('Page supprimée');
      if (activePage?.id === p.id) setActivePage(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Rows
  const openNewRow = () => {
    const blank = {};
    activePage.columns.forEach(c => { blank[c.id] = ''; });
    setRowForm(blank);
    setEditingRow(null);
    setRowDialogOpen(true);
  };

  const openEditRow = (row) => {
    setRowForm({ ...row.data });
    setEditingRow(row);
    setRowDialogOpen(true);
  };

  const handleSaveRow = async () => {
    try {
      if (editingRow) {
        await api.put(`/custom-pages/${activePage.id}/rows/${editingRow.id}`, { data: rowForm });
        toast.success('Ligne mise à jour');
      } else {
        await api.post(`/custom-pages/${activePage.id}/rows`, { data: rowForm });
        toast.success('Ligne ajoutée');
      }
      setRowDialogOpen(false);
      loadPage(activePage.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteRow = async (row) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      await api.delete(`/custom-pages/${activePage.id}/rows/${row.id}`);
      toast.success('Ligne supprimée');
      loadPage(activePage.id);
    } catch { toast.error('Erreur'); }
  };

  // Columns management
  const openColumnsDialog = () => {
    setColumnsDraft(JSON.parse(JSON.stringify(activePage.columns)));
    setNewCol({ label: '', type: 'text', options: '' });
    setColumnsDialogOpen(true);
  };

  const addCustomColumn = () => {
    if (!newCol.label.trim()) { toast.error('Libellé requis'); return; }
    const id = `c_${Date.now()}`;
    const col = {
      id, label: newCol.label.trim(), type: newCol.type,
      options: newCol.type === 'select' ? newCol.options.split(',').map(s => s.trim()).filter(Boolean) : [],
      fixed: false,
    };
    setColumnsDraft(cols => [...cols, col]);
    setNewCol({ label: '', type: 'text', options: '' });
  };

  const removeColumn = (colId) => {
    setColumnsDraft(cols => cols.filter(c => c.id !== colId || c.fixed));
  };

  const saveColumns = async () => {
    try {
      await api.put(`/custom-pages/${activePage.id}`, { columns: columnsDraft });
      toast.success('Colonnes mises à jour');
      setColumnsDialogOpen(false);
      loadPage(activePage.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="commercial-portal">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Bonjour, {user?.name || me?.contact_name || 'Commercial'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {me?.company_name} · Commission {me?.commission_pct}% sur marge nette
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} color="sky" label="Clients" value={me?.clients_count || 0} testid="kpi-clients" />
        <KpiCard icon={Briefcase} color="purple" label="Projets" value={me?.projects_count || 0} testid="kpi-projects" />
        <KpiCard icon={TrendingUp} color="emerald" label="CA généré" value={formatCurrency(me?.ca_generated || 0)} testid="kpi-ca" big />
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="kpi-commission">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-500/10 rounded-sm flex items-center justify-center"><Wallet className="w-4 h-4 text-amber-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Commissions</p>
                <p className="text-lg font-bold text-amber-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(me?.commission_total || 0)}</p>
                <p className="text-[10px] text-zinc-500">
                  <span className="text-emerald-400">{formatCurrency(me?.commission_paid || 0)} payé</span>
                  <span className="text-zinc-700 mx-1">·</span>
                  <span className="text-amber-400">{formatCurrency(me?.commission_pending || 0)} en attente</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="clients" className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">Mes clients</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">Mes projets</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">Mes commissions</TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500">
            <LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Pages perso
          </TabsTrigger>
        </TabsList>

        {/* ============ CLIENTS (table + filters) ============ */}
        <TabsContent value="clients" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input data-testid="client-search" placeholder="Rechercher société, contact, email..." value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger data-testid="client-filter-status" className="w-44 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Tous les clients</SelectItem>
                <SelectItem value="followup">À relancer (&gt;30 jours)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger data-testid="client-filter-sector" className="w-44 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue placeholder="Secteur" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Tous secteurs</SelectItem>
                {uniqueSectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-zinc-500">{filteredClients.length} / {clients.length} client(s)</div>
            <Button data-testid="new-client-btn" onClick={() => { setEditingClient(null); setClientForm(INITIAL_CLIENT); setClientDialogOpen(true); }} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              <Plus className="w-4 h-4 mr-2" /> Nouveau client
            </Button>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Société</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Contact</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Secteur</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Téléphone</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Dernière cmd.</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-center">Cmd.</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Relance</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-zinc-500">Aucun client</TableCell></TableRow>
                ) : filteredClients.map((c) => (
                  <TableRow key={c.id} className="border-zinc-800/50" data-testid={`client-row-${c.id}`}>
                    <TableCell className="text-sm font-medium text-zinc-200">{c.company_name}</TableCell>
                    <TableCell className="text-sm text-zinc-300">{c.contact_name || '-'}</TableCell>
                    <TableCell className="text-xs">{c.sector ? <span className="bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-sm">{c.sector}</span> : <span className="text-zinc-700">-</span>}</TableCell>
                    <TableCell className="text-sm text-zinc-400">{c.email || '-'}</TableCell>
                    <TableCell className="text-sm text-zinc-400">{c.phone || '-'}</TableCell>
                    <TableCell className="text-sm text-zinc-500">{c.last_order_date ? formatDate(c.last_order_date) : '—'}</TableCell>
                    <TableCell className="text-sm text-center text-zinc-300">{c.order_count || 0}</TableCell>
                    <TableCell>
                      {c.needs_followup ? (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded-sm">
                          <AlertCircle className="w-3 h-3" /> {c.days_since_order ? `${c.days_since_order}j` : 'À relancer'}
                        </span>
                      ) : <span className="text-[10px] text-emerald-500">OK</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-client-${c.id}`} onClick={() => handleEditClient(c)} className="p-1 text-zinc-500 hover:text-zinc-100"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-client-${c.id}`} onClick={() => handleDeleteClient(c.id)} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ============ PROJECTS (read-only) ============ */}
        <TabsContent value="projects">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Référence</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Projet</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Montant HT</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Commission</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-zinc-500">Aucun projet rattaché</TableCell></TableRow>
                ) : projects.map(p => (
                  <TableRow key={p.id} className="border-zinc-800/50">
                    <TableCell className="text-xs text-zinc-400 font-mono">{p.reference}</TableCell>
                    <TableCell className="text-sm text-zinc-300">{p.client_name}</TableCell>
                    <TableCell className="text-sm text-zinc-200">{p.project_name}</TableCell>
                    <TableCell><StatusBadge status={p.status} statusMap={PROJECT_STATUSES} /></TableCell>
                    <TableCell className="text-sm text-zinc-300">{formatCurrency(p.amount_ht)}</TableCell>
                    <TableCell className="text-sm">
                      <span className={`font-medium ${p.commission_paid ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {formatCurrency(p.commission_amount || 0)}
                      </span>
                      <span className="text-[10px] text-zinc-600 ml-1">{p.commission_paid ? '(payée)' : '(en attente)'}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ============ COMMISSIONS ============ */}
        <TabsContent value="commissions">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Projet</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Marge nette</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">%</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Commission</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Date paiement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-zinc-500">Aucune commission</TableCell></TableRow>
                ) : projects.map(p => (
                  <TableRow key={p.id} className="border-zinc-800/50">
                    <TableCell className="text-sm text-zinc-200">{p.project_name}<div className="text-xs text-zinc-500">{p.client_name}</div></TableCell>
                    <TableCell className={`text-sm font-medium ${(p.margin || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency(p.margin)}</TableCell>
                    <TableCell className="text-sm text-zinc-300"><Percent className="w-3 h-3 inline mr-0.5" />{p.commission_pct || 0}</TableCell>
                    <TableCell className="text-sm text-amber-400 font-medium">{formatCurrency(p.commission_amount || 0)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${p.commission_paid ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                        {p.commission_paid ? 'Payée' : 'En attente'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-500">{p.commission_paid_date ? formatDate(p.commission_paid_date) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ============ CUSTOM PAGES ============ */}
        <TabsContent value="custom" className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="back-to-pages"
              onClick={() => setActivePage(null)}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${!activePage ? 'bg-amber-500 text-zinc-950 border-amber-500' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-100'}`}>
              <LayoutGrid className="w-3.5 h-3.5 inline mr-1" /> Mes pages
            </button>
            {customPages.map(p => (
              <button
                key={p.id}
                data-testid={`page-tab-${p.id}`}
                onClick={() => loadPage(p.id)}
                className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${activePage?.id === p.id ? 'bg-amber-500 text-zinc-950 border-amber-500' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-100'}`}>
                <FileText className="w-3.5 h-3.5 inline mr-1" /> {p.name}
              </button>
            ))}
            {customPages.length < 5 && (
              <button
                data-testid="add-page-btn"
                onClick={() => setPageDialogOpen(true)}
                className="px-3 py-1.5 rounded-sm text-xs font-medium border border-dashed border-zinc-700 text-zinc-500 hover:text-amber-400 hover:border-amber-500/50 transition-colors">
                <Plus className="w-3.5 h-3.5 inline mr-1" /> Nouvelle page
              </button>
            )}
            <span className="text-[10px] text-zinc-600 ml-1">{customPages.length}/5</span>
          </div>

          {!activePage ? (
            customPages.length === 0 ? (
              <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-sm">
                <LayoutGrid className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                <p className="text-zinc-400 mb-1">Créez votre première page personnalisée</p>
                <p className="text-xs text-zinc-600 mb-4">Suivi prospects, notes de tournée, actions à faire... organisez votre démarchage à votre façon.</p>
                <Button data-testid="first-page-btn" onClick={() => setPageDialogOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
                  <Plus className="w-4 h-4 mr-2" /> Créer ma première page
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customPages.map(p => (
                  <Card key={p.id} className="bg-zinc-900 border-zinc-800 rounded-sm cursor-pointer hover:border-zinc-700 transition-colors" onClick={() => loadPage(p.id)} data-testid={`page-card-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-amber-400" />
                          <h3 className="text-base font-semibold text-zinc-100">{p.name}</h3>
                        </div>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <button data-testid={`rename-page-${p.id}`} onClick={() => { setRenameDialog(p); setRenameValue(p.name); }} className="p-1 text-zinc-500 hover:text-zinc-100"><Pencil className="w-3 h-3" /></button>
                          <button data-testid={`delete-page-${p.id}`} onClick={() => handleDeletePage(p)} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <p className="text-xs text-zinc-500"><TableIcon className="w-3 h-3 inline mr-1" />{p.row_count || 0} ligne(s) · {p.columns?.length || 0} colonne(s)</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : (
            <CustomPageView
              page={activePage}
              onNewRow={openNewRow}
              onEditRow={openEditRow}
              onDeleteRow={handleDeleteRow}
              onManageColumns={openColumnsDialog}
              onRename={() => { setRenameDialog(activePage); setRenameValue(activePage.name); }}
              onDelete={() => handleDeletePage(activePage)}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* ============ Dialogs ============ */}
      <ClientDialog open={clientDialogOpen} onOpenChange={setClientDialogOpen} editing={editingClient} form={clientForm} setForm={setClientForm} onSave={handleSaveClient} />

      <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="page-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle page</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Ex: "Prospects chauds", "Appels à faire", "Salon Paris"</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label className="text-zinc-400 text-sm">Nom de la page *</Label>
            <Input data-testid="new-page-name" value={pageForm.name} onChange={(e) => setPageForm({ name: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && handleCreatePage()} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" autoFocus />
            <p className="text-[11px] text-zinc-600">La page sera créée avec 3 colonnes de base (Titre, Date, Statut). Vous pourrez ajouter d'autres colonnes personnalisées ensuite.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPageDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-page-btn" onClick={handleCreatePage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameDialog} onOpenChange={(o) => !o && setRenameDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle>Renommer la page</DialogTitle></DialogHeader>
          <div className="py-4">
            <Input data-testid="rename-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRenamePage()} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-rename-btn" onClick={handleRenamePage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RowDialog
        open={rowDialogOpen}
        onOpenChange={setRowDialogOpen}
        page={activePage}
        form={rowForm}
        setForm={setRowForm}
        editing={editingRow}
        onSave={handleSaveRow}
      />

      <ColumnsDialog
        open={columnsDialogOpen}
        onOpenChange={setColumnsDialogOpen}
        columns={columnsDraft}
        newCol={newCol}
        setNewCol={setNewCol}
        onAdd={addCustomColumn}
        onRemove={removeColumn}
        onSave={saveColumns}
      />
    </div>
  );
}

// ================= Components =================

function KpiCard({ icon: Icon, color, label, value, testid, big }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 bg-${color}-500/10 rounded-sm flex items-center justify-center`}><Icon className={`w-4 h-4 text-${color}-400`} /></div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
            <p className={`${big ? 'text-lg' : 'text-xl'} font-bold text-${color}-400`} style={{fontFamily:'Cabinet Grotesk'}}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ClientDialog({ open, onOpenChange, editing, form, setForm, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Modifier client' : 'Nouveau client'}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">Entrez les informations du prospect / client.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2 space-y-2">
            <Label className="text-zinc-400 text-sm">Société *</Label>
            <Input data-testid="form-client-company" value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
          </div>
          <div className="space-y-2"><Label className="text-zinc-400 text-sm">Contact</Label><Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
          <div className="space-y-2"><Label className="text-zinc-400 text-sm">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
          <div className="space-y-2"><Label className="text-zinc-400 text-sm">Téléphone</Label><Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
          <div className="space-y-2"><Label className="text-zinc-400 text-sm">Secteur</Label><Input value={form.sector} onChange={(e) => setForm(f => ({ ...f, sector: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
          <div className="space-y-2"><Label className="text-zinc-400 text-sm">SIRET</Label><Input value={form.siret} onChange={(e) => setForm(f => ({ ...f, siret: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
          <div className="col-span-2 space-y-2"><Label className="text-zinc-400 text-sm">Adresse</Label><Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
          <div className="col-span-2 space-y-2"><Label className="text-zinc-400 text-sm">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
          <Button data-testid="save-client-btn" onClick={onSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">{editing ? 'Mettre à jour' : 'Créer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomPageView({ page, onNewRow, onEditRow, onDeleteRow, onManageColumns, onRename, onDelete }) {
  const [search, setSearch] = useState('');
  const [filterCol, setFilterCol] = useState('all');
  const [filterVal, setFilterVal] = useState('all');

  const rows = page.rows || [];
  const filtered = rows.filter(r => {
    const haystack = Object.values(r.data || {}).map(v => String(v || '').toLowerCase()).join(' ');
    if (search && !haystack.includes(search.toLowerCase())) return false;
    if (filterCol !== 'all' && filterVal !== 'all' && String(r.data?.[filterCol] || '') !== filterVal) return false;
    return true;
  });

  const filterableCols = page.columns.filter(c => c.type === 'select' && c.options?.length);
  const activeFilterCol = page.columns.find(c => c.id === filterCol);

  return (
    <div className="space-y-4" data-testid={`page-view-${page.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{page.name}</h2>
        <span className="text-xs text-zinc-600">{rows.length} ligne(s)</span>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="manage-columns-btn" onClick={onManageColumns} className="text-xs px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-100 rounded-sm">
            <Columns className="w-3.5 h-3.5 inline mr-1" /> Colonnes
          </button>
          <button data-testid="page-rename-btn" onClick={onRename} className="text-xs px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-100 rounded-sm">
            <Pencil className="w-3.5 h-3.5 inline mr-1" /> Renommer
          </button>
          <button data-testid="page-delete-btn" onClick={onDelete} className="text-xs px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-rose-400 rounded-sm">
            <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Supprimer
          </button>
          <Button data-testid="new-row-btn" onClick={onNewRow} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium h-8">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle ligne
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="row-search" placeholder="Rechercher dans ce tableau..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" />
        </div>
        {filterableCols.length > 0 && (
          <>
            <Select value={filterCol} onValueChange={(v) => { setFilterCol(v); setFilterVal('all'); }}>
              <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Colonne" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Toutes colonnes</SelectItem>
                {filterableCols.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {activeFilterCol && (
              <Select value={filterVal} onValueChange={setFilterVal}>
                <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Valeur" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Toutes valeurs</SelectItem>
                  {activeFilterCol.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {page.columns.map(c => (
                <TableHead key={c.id} className="text-xs uppercase tracking-wider text-zinc-500">{c.label}</TableHead>
              ))}
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={page.columns.length + 1} className="text-center py-12 text-zinc-500">Aucune ligne {search || filterVal !== 'all' ? '(filtre actif)' : '— cliquez sur "Nouvelle ligne" pour commencer'}</TableCell></TableRow>
            ) : filtered.map(r => (
              <TableRow key={r.id} className="border-zinc-800/50" data-testid={`row-${r.id}`}>
                {page.columns.map(c => (
                  <TableCell key={c.id} className="text-sm text-zinc-300">
                    {c.type === 'date' && r.data?.[c.id] ? formatDate(r.data[c.id]) :
                     c.type === 'select' && r.data?.[c.id] ? <span className="inline-flex px-2 py-0.5 rounded-sm text-xs bg-zinc-800 text-amber-400 border border-zinc-700">{r.data[c.id]}</span> :
                     r.data?.[c.id] || <span className="text-zinc-700">-</span>}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`edit-row-${r.id}`} onClick={() => onEditRow(r)} className="p-1 text-zinc-500 hover:text-zinc-100"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-row-${r.id}`} onClick={() => onDeleteRow(r)} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RowDialog({ open, onOpenChange, page, form, setForm, editing, onSave }) {
  if (!page) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="row-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Modifier la ligne' : 'Nouvelle ligne'}</DialogTitle></DialogHeader>
        <div className="py-4 space-y-3">
          {page.columns.map(c => (
            <div key={c.id} className="space-y-1.5">
              <Label className="text-zinc-400 text-sm">{c.label}</Label>
              {c.type === 'select' ? (
                <Select value={form[c.id] || ''} onValueChange={(v) => setForm(f => ({ ...f, [c.id]: v }))}>
                  <SelectTrigger data-testid={`row-input-${c.id}`} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {c.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : c.type === 'number' ? (
                <Input data-testid={`row-input-${c.id}`} type="number" step="0.01" value={form[c.id] || ''} onChange={(e) => setForm(f => ({ ...f, [c.id]: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              ) : c.type === 'date' ? (
                <Input data-testid={`row-input-${c.id}`} type="date" value={form[c.id] || ''} onChange={(e) => setForm(f => ({ ...f, [c.id]: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              ) : (
                <Input data-testid={`row-input-${c.id}`} value={form[c.id] || ''} onChange={(e) => setForm(f => ({ ...f, [c.id]: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
          <Button data-testid="save-row-btn" onClick={onSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">{editing ? 'Mettre à jour' : 'Ajouter'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColumnsDialog({ open, onOpenChange, columns, newCol, setNewCol, onAdd, onRemove, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="columns-dialog">
        <DialogHeader>
          <DialogTitle>Gérer les colonnes</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">Les colonnes "Titre", "Date" et "Statut" sont fixes et ne peuvent pas être supprimées.</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <div className="space-y-1.5">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Colonnes actuelles</p>
            {columns.map(c => (
              <div key={c.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-200">{c.label}</span>
                  <span className="text-[10px] text-zinc-500 uppercase">{COLUMN_TYPE_LABELS[c.type]}</span>
                  {c.fixed && <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 rounded-sm">fixe</span>}
                </div>
                {!c.fixed && (
                  <button data-testid={`remove-col-${c.id}`} onClick={() => onRemove(c.id)} className="text-zinc-500 hover:text-rose-400">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="pt-3 border-t border-zinc-800 space-y-2">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Ajouter une colonne</p>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="new-col-label" placeholder="Libellé" value={newCol.label} onChange={(e) => setNewCol(n => ({ ...n, label: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" />
              <Select value={newCol.type} onValueChange={(v) => setNewCol(n => ({ ...n, type: v }))}>
                <SelectTrigger data-testid="new-col-type" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="text">Texte</SelectItem>
                  <SelectItem value="number">Nombre</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="select">Liste déroulante</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newCol.type === 'select' && (
              <Input data-testid="new-col-options" placeholder="Options séparées par virgule — ex: Froid, Tiède, Chaud" value={newCol.options} onChange={(e) => setNewCol(n => ({ ...n, options: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" />
            )}
            <Button data-testid="add-col-btn" onClick={onAdd} variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm h-8 text-xs">
              <Plus className="w-3 h-3 mr-1" /> Ajouter cette colonne
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
          <Button data-testid="save-columns-btn" onClick={onSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">Enregistrer les colonnes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
